/* eslint-disable react-hooks/rules-of-hooks */
import { useMutation, type ApolloCache } from "@apollo/client";

import {
  ASSIGN_INVENTORY_PARTS_TO_SUPPORT_TICKET_MUTATION,
  BULK_DELETE_OWN_OEM_SUPPORT_TICKETS_MUTATION,
  CREATE_OWN_OEM_SUPPORT_TICKET_MUTATION,
  DE_ESCALATE_SUPPORT_TICKET_MUTATION,
  DELETE_OWN_OEM_SUPPORT_TICKET_MUTATION,
  EDIT_INVENTORY_PART_ON_SUPPORT_TICKET_MUTATION,
  LINK_SUPPORT_TICKET_TO_WORK_ORDER_MUTATION,
  REMOVE_INVENTORY_PART_FROM_SUPPORT_TICKET_MUTATION,
  UNLINK_SUPPORT_TICKET_FROM_WORK_ORDER_MUTATION,
  UPDATE_OWN_OEM_SUPPORT_TICKET_MUTATION,
  UPDATE_OWN_OEM_SUPPORT_TICKET_TYPE_MUTATION,
} from "~/api";

interface SupportTicketWorkOrderLinkVars {
  supportTicketId: string;
  workOrderId: string;
}

export interface CreateOwnOemSupportTicketInput {
  title: string;
  type: string;
  companyId: string;
  assetIds?: string[];
  /** Required by the backend on create (GraphQL `String!`); whitespace-only is rejected client-side. */
  description: string;
  inventoryParts?: Array<{
    part: string;
    addedBy: string;
    assetId?: string;
    quantity: number;
  }>;
  customFields?: Array<Record<string, unknown>>;
  threadId?: string | null;
  isCreatorInternal: boolean;
  /** Source work order when creating a ticket from a Work Order — the backend links atomically. */
  workOrderId?: string;
}

export interface UpdateOwnOemSupportTicketInput {
  supportTicketId: string;
  title?: string;
  description?: string;
  status?: string;
  type?: string;
  company?: string;
  assets?: string[];
  assignees?: string[];
  customFields?: Array<Record<string, unknown>>;
}

// List/kanban/total caches to drop after a write so the views re-fetch.
const evictSupportTicketListCaches = (cache: ApolloCache<unknown>) => {
  [
    "listOwnOemSupportTickets",
    "listOwnOemKanbanSupportTickets",
    "totalSupportTickets",
  ].forEach((fieldName) => cache.evict({ id: "ROOT_QUERY", fieldName }));
  cache.gc();
};

// The Activity Log tab watches `listOwnOemActivityLogs` with a `cache-and-network`
// policy, so it only re-fetches when the cached field is dropped. Every write that
// the support-ticket trigger logs must evict it, otherwise the tab keeps showing
// the list it loaded on mount and the user has to reload the page. Mirrors
// `evictOwnOemActivityLogs` in `services/workOrders/mutations.ts`.
const evictOwnOemActivityLogs = (cache: ApolloCache<unknown>) => {
  cache.evict({ id: "ROOT_QUERY", fieldName: "listOwnOemActivityLogs" });
  cache.gc();
};

// Attaching/detaching an asset (or swapping the company) re-derives the
// ticket's `teams` server-side, which changes who may be assigned. The returned
// `SupportTicketFull` refreshes the team members shown in the assignee
// dropdown, but `listSupportTicketAssignableUsers` is a separate root field —
// without dropping it the dropdown lists the new team's members and renders
// them all disabled until a page reload.
//
// The same writes move `listOwnSupportTicketMentionableUsers`: the asset's
// company is what puts facility (external) users in the mention list, so a
// ticket that gains its first asset keeps an OEM-only @-list — and the chat
// composer offers nobody external — until a reload.
const evictSupportTicketTeamDerivedUsers = (cache: ApolloCache<unknown>) => {
  cache.evict({
    id: "ROOT_QUERY",
    fieldName: "listSupportTicketAssignableUsers",
  });
  cache.evict({
    id: "ROOT_QUERY",
    fieldName: "listOwnSupportTicketMentionableUsers",
  });
  cache.gc();
};

export const useCreateOwnOemSupportTicket = () => {
  const [createOwnOemSupportTicket, { error: { networkError } = {}, loading }] =
    useMutation(CREATE_OWN_OEM_SUPPORT_TICKET_MUTATION);

  return {
    mutation: (input: CreateOwnOemSupportTicketInput) =>
      createOwnOemSupportTicket({
        variables: { input },
        update(cache) {
          evictSupportTicketListCaches(cache);
        },
      }),
    loading,
    serverErrors: networkError,
  };
};

export const useUpdateOwnOemSupportTicket = () => {
  const [updateOwnOemSupportTicket, { error: { networkError } = {}, loading }] =
    useMutation(UPDATE_OWN_OEM_SUPPORT_TICKET_MUTATION);

  return {
    mutation: (
      input: UpdateOwnOemSupportTicketInput,
      { skipCacheUpdate = false }: { skipCacheUpdate?: boolean } = {},
    ) =>
      updateOwnOemSupportTicket({
        variables: { input },
        // `unaffiliatedAssetLinkedTo` is derived server-side across the whole
        // linked group (this ticket + its work orders), and the mutation's
        // `SupportTicketFull` payload doesn't carry the re-derived value — so
        // attaching an unaffiliated asset left the sidebar showing an enabled
        // "assign company" trigger and an unrestricted asset picker until a page
        // reload. Only `assets`/`company` writes can flip it; every other write
        // (status, title, custom fields) skips the round trip.
        ...(input.assets || input.company
          ? {
              refetchQueries: ["getOwnOemSupportTicketById"],
              awaitRefetchQueries: true,
            }
          : {}),
        update(cache) {
          evictOwnOemActivityLogs(cache);

          // Inline table edits (custom fields) pass `skipCacheUpdate`: the write
          // can't change ordering or column membership, and dropping the list
          // caches would refetch the whole table under the user mid-scroll.
          if (skipCacheUpdate) return;

          // Status/type/company/assets/assignees changes affect ordering and
          // column membership; the single-ticket cache updates via normalized id.
          evictSupportTicketListCaches(cache);

          // `assets`/`company` writes re-derive the ticket's teams, which drive
          // both the assignable and the mentionable user lists.
          if (input.assets || input.company) {
            evictSupportTicketTeamDerivedUsers(cache);
          }
        },
      }),
    loading,
    serverErrors: networkError,
  };
};

/**
 * Change a support ticket's type, plus the fields that type drives (title,
 * parts, custom fields), in one server-validated transaction.
 *
 * Reconciliation is the server's, and differs per key:
 * - `inventoryParts` **replaces** every line when present; omit the key
 *   entirely and existing lines are left alone. Lines are identified by
 *   (part, `assetId`) — `assetId` is omitted for ticket-wide lines on an
 *   asset-less ticket, and auto-resolved when exactly one attached asset owns
 *   the part. No `addedBy`: the server stamps it from the caller.
 * - `customFields` are **merged** by `fieldId`, never pruned, so values for
 *   fields the target type doesn't render survive the switch.
 * - `description` is written only when a non-empty value is passed AND the
 *   target type shows the field. We never pass it: a support ticket's
 *   description is its first chat message, owned by the chat surface.
 *
 * The target type's required fields, `isSystem`, and the internal/external
 * constraint are all enforced server-side; the form restricts the dropdown to
 * matching types as defence in depth.
 */
export interface UpdateOwnOemSupportTicketTypeInput {
  supportTicketId: string;
  /** Target support ticket type id. Named `type`, not `ticketType`. */
  type: string;
  /** Omit to keep the existing title. */
  title?: string;
  inventoryParts?: Array<{
    part: string;
    quantity: number;
    assetId?: string;
  }>;
  customFields?: Array<Record<string, unknown>>;
}

export const useUpdateOwnOemSupportTicketType = () => {
  const [updateSupportTicketType, { error: { networkError } = {}, loading }] =
    useMutation(UPDATE_OWN_OEM_SUPPORT_TICKET_TYPE_MUTATION);

  return {
    mutation: (input: UpdateOwnOemSupportTicketTypeInput) =>
      updateSupportTicketType({
        variables: { input },
        update(cache) {
          // A type change moves the ticket between kanban columns and reorders
          // the lists; the single-ticket cache updates via its normalized id
          // from the returned `SupportTicketFull`.
          evictSupportTicketListCaches(cache);
          evictOwnOemActivityLogs(cache);
        },
      }),
    loading,
    serverErrors: networkError,
  };
};

export const useAssignInventoryPartsToSupportTicket = () => {
  const [assignParts, { loading }] = useMutation(
    ASSIGN_INVENTORY_PARTS_TO_SUPPORT_TICKET_MUTATION,
  );

  return {
    loading,
    assignParts: ({
      supportTicketId,
      inventoryParts,
    }: {
      supportTicketId: string;
      // `assetId` targets a specific attached asset's BOM line. Optional: the
      // backend infers it when the part belongs to exactly one attached asset,
      // and it's absent entirely on asset-less tickets.
      inventoryParts: Array<{
        part: string;
        quantity: number;
        assetId?: string;
      }>;
    }) =>
      assignParts({
        variables: {
          input: {
            supportTicketId,
            inventoryParts: inventoryParts.map(
              ({ part, quantity, assetId }) => ({
                part,
                quantity,
                ...(assetId ? { assetId } : {}),
              }),
            ),
          },
        },
        update(cache) {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useRemoveInventoryPartFromSupportTicket = () => {
  const [removePart, { loading }] = useMutation(
    REMOVE_INVENTORY_PART_FROM_SUPPORT_TICKET_MUTATION,
  );

  return {
    loading,
    // With `assetId` only the (part, assetId) line is removed; without it, all
    // lines of that part across assets are removed (legacy behavior).
    removePart: ({
      supportTicketId,
      partId,
      assetId,
    }: {
      supportTicketId: string;
      partId: string;
      assetId?: string;
    }) =>
      removePart({
        variables: {
          input: { supportTicketId, partId, ...(assetId ? { assetId } : {}) },
        },
        update(cache) {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useEditInventoryPartOnSupportTicket = () => {
  const [editPart, { loading }] = useMutation(
    EDIT_INVENTORY_PART_ON_SUPPORT_TICKET_MUTATION,
  );

  return {
    loading,
    // With `assetId` only the (part, assetId) line's quantity is edited;
    // without it, all lines of that part are affected (legacy behavior).
    editPart: ({
      supportTicketId,
      partId,
      quantity,
      assetId,
    }: {
      supportTicketId: string;
      partId: string;
      quantity: number;
      assetId?: string;
    }) =>
      editPart({
        variables: {
          input: {
            supportTicketId,
            partId,
            quantity,
            ...(assetId ? { assetId } : {}),
          },
        },
        update(cache) {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

// Links/unlinks a work order to a support ticket. Both directions use the same
// pair of mutations. The mutation returns the full SupportTicket, so the
// support ticket's own `linkedWorkOrders` updates via the normalized cache; the
// work order's reverse `linkedSupportTickets` field is computed server-side, so
// callers on the work-order side should `refetch()` their work order afterwards.
export const useLinkSupportTicketToWorkOrder = () => {
  const [linkMutation, { loading }] = useMutation(
    LINK_SUPPORT_TICKET_TO_WORK_ORDER_MUTATION,
  );

  return {
    loading,
    link: ({ supportTicketId, workOrderId }: SupportTicketWorkOrderLinkVars) =>
      linkMutation({
        variables: { supportTicketId, workOrderId },
        update(cache) {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useUnlinkSupportTicketFromWorkOrder = () => {
  const [unlinkMutation, { loading }] = useMutation(
    UNLINK_SUPPORT_TICKET_FROM_WORK_ORDER_MUTATION,
  );

  return {
    loading,
    unlink: ({
      supportTicketId,
      workOrderId,
    }: SupportTicketWorkOrderLinkVars) =>
      unlinkMutation({
        variables: { supportTicketId, workOrderId },
        update(cache) {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

/**
 * Hand an escalated ticket back down to a portal (Owners/Admins only).
 *
 * `connectionId` is the receiving portal, chosen from
 * `listSupportTicketDeEscalationTargets` — it need not be the portal that
 * escalated the ticket. Who deals with a customer is the OEM's call.
 *
 * On success the ticket leaves EVERY OEM list, the caller's included, so the
 * list caches have to go. The returned `escalatedToOem` / `handedTo` update the
 * open detail through the normalized cache, which is why the detail view can
 * stay put instead of bouncing the user to a list the ticket just left.
 * Owners/Admins can still reach it from the asset's History; Supervisors and
 * Technicians lose it entirely.
 */
export const useDeEscalateSupportTicket = () => {
  const [deEscalate, { loading }] = useMutation(
    DE_ESCALATE_SUPPORT_TICKET_MUTATION,
  );

  return {
    loading,
    deEscalateTicket: ({
      supportTicketId,
      connectionId,
    }: {
      supportTicketId: string;
      connectionId: string;
    }) =>
      deEscalate({
        variables: { input: { supportTicketId, connectionId } },
        update(cache) {
          evictSupportTicketListCaches(cache);
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useDeleteOwnOemSupportTicket = () => {
  const [deleteOwnOemSupportTicket, { loading }] = useMutation(
    DELETE_OWN_OEM_SUPPORT_TICKET_MUTATION,
  );

  return {
    loading,
    removeTicket: ({
      supportTicketId,
      reason,
    }: {
      supportTicketId: string;
      reason?: string;
    }) =>
      deleteOwnOemSupportTicket({
        variables: { input: { supportTicketId, reason } },
        update(cache) {
          evictSupportTicketListCaches(cache);
          cache.evict({
            id: cache.identify({
              __typename: "SupportTicket",
              _id: supportTicketId,
            }),
          });
          cache.gc();
        },
      }),
  };
};

export const useBulkDeleteSupportTickets = () => {
  const [bulkDelete] = useMutation(
    BULK_DELETE_OWN_OEM_SUPPORT_TICKETS_MUTATION,
    {
      update(cache) {
        evictSupportTicketListCaches(cache);
        cache.gc();
      },
    },
  );

  const bulkDeleteSupportTickets = async (ids: string[]) => {
    const { data } = await bulkDelete({ variables: { ids } });
    return data.bulkDeleteOwnOemSupportTickets;
  };

  return { bulkDeleteSupportTickets };
};
